import { useEffect, useRef } from "react";
import { Grid } from "@mui/material";
import InputField from "../../../../components/Common/Input";
import { StyledError } from "../../../../components/styles";
import Button from "../../../../components/Common/Button";
import CreatUserForm from "./ContactUsForm";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ContactUs = () => {
  const formik = CreatUserForm();
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const form = formRef.current;
    const info = infoRef.current;

    if (!section || !header || !form) return;

    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        header.querySelectorAll(".sectionLabel, h2, .subtitle"),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Form card animation
      gsap.fromTo(
        form,
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: form,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Info cards animation
      if (info) {
        gsap.fromTo(
          info.querySelectorAll(".infoCard"),
          { opacity: 0, x: 30 },
          {
            opacity: 1,
            x: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: info,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <StyledContact ref={sectionRef} id="contactUs">
      <div className="contactContainer">
        <div className="sectionHeader" ref={headerRef}>
          <div className="sectionLabel">
            <span className="line" />
            <span>Get In Touch</span>
          </div>
          <h2>
            Let's Start a <span className="highlight">Conversation</span>
          </h2>
          <p className="subtitle">
            Have a project in mind? We'd love to hear from you. Send us a
            message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="contactContent">
          <form
            className="contactForm"
            method="post"
            name="contactForm"
            ref={formRef}
          >
            <div className="formHeader">
              <h3>Send us a message</h3>
              <p>Fill out the form below and we'll get back to you shortly.</p>
            </div>

            <Grid spacing={3} container>
              <Grid xs={12} sm={6} item>
                <div className="inputGroup">
                  <label>Your Name</label>
                  <InputField
                    formik={formik}
                    name="name"
                    placeholder="John Doe"
                  />
                  <StyledError>
                    {formik.touched?.name && formik.errors["name"]}
                  </StyledError>
                </div>
              </Grid>
              <Grid xs={12} sm={6} item>
                <div className="inputGroup">
                  <label>Email Address</label>
                  <InputField
                    formik={formik}
                    name="email"
                    placeholder="john@example.com"
                  />
                  <StyledError>
                    {formik.touched?.email && formik.errors["email"]}
                  </StyledError>
                </div>
              </Grid>
              <Grid xs={12} sm={6} item>
                <div className="inputGroup">
                  <label>Your Address</label>
                  <InputField
                    formik={formik}
                    name="address"
                    placeholder="Your address"
                  />
                  <StyledError>
                    {formik.touched?.address && formik.errors["address"]}
                  </StyledError>
                </div>
              </Grid>
              <Grid xs={12} sm={6} item>
                <div className="inputGroup">
                  <label>Phone Number</label>
                  <InputField
                    formik={formik}
                    name="phoneNumber"
                    placeholder="+965 XXXX XXXX"
                  />
                  <StyledError>
                    {formik.touched?.phoneNumber && formik.errors["phoneNumber"]}
                  </StyledError>
                </div>
              </Grid>
              <Grid xs={12} item>
                <div className="inputGroup">
                  <label>Your Message</label>
                  <InputField
                    formik={formik}
                    name="description"
                    placeholder="Tell us about your project..."
                    multiline
                  />
                  <StyledError>
                    {formik.touched?.description && formik.errors["description"]}
                  </StyledError>
                </div>
              </Grid>
              <Grid xs={12} item>
                <Button
                  onClick={formik?.handleSubmit}
                  disabled={formik.isSubmitting}
                >
                  Send Message
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, marginLeft: 8 }}>
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </Button>
              </Grid>
            </Grid>
          </form>

          <div className="contactInfo" ref={infoRef}>
            <div className="infoCard">
              <div className="iconWrapper">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div className="infoContent">
                <h4>Our Location</h4>
                <p>Hawally, Kuwait</p>
              </div>
            </div>

            <div className="infoCard">
              <div className="iconWrapper">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                </svg>
              </div>
              <div className="infoContent">
                <h4>Phone Number</h4>
                <p>+965 2264 5937</p>
                <p>+965 5060 4047</p>
              </div>
            </div>

            <div className="infoCard">
              <div className="iconWrapper">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div className="infoContent">
                <h4>Email Address</h4>
                <p>sales@saharacomputers.com</p>
              </div>
            </div>

            <div className="infoCard highlight">
              <div className="highlightContent">
                <h4>Business Hours</h4>
                <p>Saturday - Thursday: 9AM - 8PM</p>
                <p>Friday: Closed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="decorCircle circle1" />
      <div className="decorCircle circle2" />
    </StyledContact>
  );
};

export default ContactUs;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
`;

const StyledContact = styled.section`
  background: var(--bg-secondary);
  padding: 140px 20px;
  position: relative;
  overflow: hidden;

  .decorCircle {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;

    &.circle1 {
      width: 400px;
      height: 400px;
      background: linear-gradient(135deg, rgba(217, 58, 53, 0.1) 0%, transparent 70%);
      top: -100px;
      right: -100px;
      animation: ${float} 10s ease-in-out infinite;
    }

    &.circle2 {
      width: 300px;
      height: 300px;
      background: linear-gradient(135deg, rgba(43, 59, 124, 0.1) 0%, transparent 70%);
      bottom: -50px;
      left: -50px;
      animation: ${float} 8s ease-in-out infinite reverse;
    }
  }

  .contactContainer {
    max-width: 1400px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .sectionHeader {
    text-align: center;
    margin-bottom: 80px;

    .sectionLabel {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      justify-content: center;

      .line {
        width: 40px;
        height: 3px;
        background: var(--accent);
        border-radius: 2px;
      }

      span {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 3px;
        color: var(--accent);
      }
    }

    h2 {
      font-family: Poppins;
      font-size: 56px;
      font-weight: 800;
      line-height: 1.1;
      color: var(--text-primary);
      margin: 0 0 20px;

      .highlight {
        background: linear-gradient(135deg, var(--accent) 0%, var(--primary) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    .subtitle {
      font-family: Poppins;
      font-size: 18px;
      line-height: 1.7;
      color: var(--text-secondary);
      max-width: 600px;
      margin: 0 auto;
    }
  }

  .contactContent {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 60px;
    align-items: start;
  }

  .contactForm {
    background: var(--card-bg);
    border-radius: 24px;
    padding: 48px;
    border: 2px solid var(--card-border);
    box-shadow: 0 20px 60px var(--shadow-medium);

    .formHeader {
      margin-bottom: 32px;

      h3 {
        font-family: Poppins;
        font-size: 28px;
        font-weight: 700;
        color: var(--text-primary);
        margin: 0 0 8px;
      }

      p {
        font-family: Poppins;
        font-size: 14px;
        color: var(--text-secondary);
        margin: 0;
      }
    }

    .inputGroup {
      label {
        display: block;
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 8px;
      }
    }

    button {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 18px 36px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 14px;
      margin-top: 16px;
    }
  }

  .contactInfo {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .infoCard {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 24px;
      background: var(--card-bg);
      border-radius: 16px;
      border: 2px solid var(--card-border);
      transition: all 0.3s ease;

      &:hover {
        border-color: var(--primary);
        transform: translateX(10px);
      }

      &.highlight {
        background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
        border: none;
        padding: 32px;

        .highlightContent {
          h4 {
            color: #fff;
            font-family: Poppins;
            font-size: 20px;
            font-weight: 700;
            margin: 0 0 12px;
          }

          p {
            color: rgba(255, 255, 255, 0.9);
            font-family: Poppins;
            font-size: 14px;
            margin: 4px 0;
          }
        }
      }

      .iconWrapper {
        width: 56px;
        height: 56px;
        border-radius: 14px;
        background: linear-gradient(135deg, rgba(43, 59, 124, 0.1) 0%, rgba(217, 58, 53, 0.1) 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          width: 24px;
          height: 24px;
          color: var(--primary);
        }
      }

      .infoContent {
        h4 {
          font-family: Poppins;
          font-size: 16px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0 0 4px;
        }

        p {
          font-family: Poppins;
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0;
        }
      }
    }
  }

  @media (max-width: 1024px) {
    padding: 100px 20px;

    .sectionHeader h2 {
      font-size: 42px;
    }

    .contactContent {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .contactForm {
      padding: 32px;
    }

    .contactInfo {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 640px) {
    padding: 80px 20px;

    .sectionHeader h2 {
      font-size: 32px;
    }

    .contactForm {
      padding: 24px;

      .formHeader h3 {
        font-size: 22px;
      }
    }

    .contactInfo {
      grid-template-columns: 1fr;
    }
  }
`;
